<template>
  <v-container fluid fill-height>
    <v-layout
      v-if="!list.length && reading"
      fill-height
      justify-center
      align-center
    >
      <v-progress-circular indeterminate />
    </v-layout>
    <v-layout v-else>
      <v-flex xs12 sm10 offset-sm1 md8 offset-md2>
        <v-layout fill-height column justify-space-between>
          <v-layout column>
            <v-flex>
              <v-slide-y-transition mode="out-in">
                <v-layout v-if="filter.open" wrap align-end class="mb-4">
                  <v-flex class="flex-grow-0 mr-2">
                    <v-text-field
                      v-model="filter.search"
                      label="Search"
                      autofocus
                      clearable
                    />
                  </v-flex>
                  <v-flex class="flex-grow-0">
                    <v-select
                      v-model="filter.ctaTypes"
                      :items="ctaTypes"
                      label="Call to action"
                      multiple
                      chips
                      deletable-chips
                      item-text="name"
                      item-value="id"
                    />
                  </v-flex>
                </v-layout>
                <v-btn v-else @click="filter.open = !filter.open" flat>
                  <v-layout justify-start>
                    <v-icon>search</v-icon>
                  </v-layout>
                </v-btn>
              </v-slide-y-transition>
              <v-layout class="mb-4" justify-space-between>
                <v-btn ripple flat @click="beginEdit(null)">
                  <v-icon left>add</v-icon> Add FAQ
                </v-btn>
                <v-hover>
                  <v-btn
                    slot-scope="{ hover }"
                    flat
                    :loading="reading"
                    :disabled="reading"
                    @click="read($route.params.typeId)"
                  >
                    <v-fade-transition mode="out-in">
                      <v-icon v-if="hover">refresh</v-icon>
                      <v-icon v-else>check_circle</v-icon>
                    </v-fade-transition>
                  </v-btn>
                </v-hover>
              </v-layout>
              <v-layout
                v-for="item in paged"
                :key="`key-${item.id}`"
                justify-space-between
                :column="$vuetify.breakpoint.xs"
                class="my-2 background-white box-shadow"
              >
                <v-flex column class="width-100 py-2 px-3">
                  <p class="subheading my-1">{{ item.question }}</p>
                  <p class="my-1">{{ item.answer }}</p>
                  <v-divider class="my-2" />
                  <a
                    class="display-block my-1 black--text text-decoration-none black--text focus-background-grey"
                    :href="item.external_link"
                    target="_blank"
                  >
                    {{ item.external_link }}
                  </a>
                  <p v-if="item.cta_type" class="my-1">
                    {{ ctaTypeLabel(item.cta_type) }}: {{ item.cta_value }}
                  </p>
                  <p class="my-1">{{ item.status ? "Active" : "Inactive" }}</p>
                </v-flex>
                <v-slide-x-transition mode="out-in">
                  <v-flex v-if="toRemove.id === item.id" key="options">
                    <v-layout justify-end>
                      <v-btn
                        flat
                        :loading="removing"
                        :disabled="removing"
                        @click.stop="remove(item)"
                      >
                        Delete
                      </v-btn>
                      <v-btn
                        flat
                        :disabled="removing"
                        @click.stop="setToRemove(null)"
                      >
                        Cancel
                      </v-btn>
                    </v-layout>
                  </v-flex>
                  <v-flex v-else key="confirm-delete">
                    <v-layout justify-end>
                      <v-tooltip bottom>
                        <v-btn
                          slot="activator"
                          icon
                          ripple
                          @click.stop="beginEdit(item)"
                        >
                          <v-icon>create</v-icon>
                        </v-btn>
                        <span>Edit</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <v-btn
                          slot="activator"
                          icon
                          ripple
                          @click.stop="setToRemove(item)"
                        >
                          <v-icon>delete</v-icon>
                        </v-btn>
                        <span>Delete</span>
                      </v-tooltip>
                    </v-layout>
                  </v-flex>
                </v-slide-x-transition>
              </v-layout>
            </v-flex>
          </v-layout>
          <v-layout column class="flex-grow-0">
            <v-flex>
              <v-layout v-if="filtered.length > pageSize" justify-center>
                <v-pagination v-model="currentPage" :length="totalPages" />
              </v-layout>
              <v-layout justify-space-between align-center>
                <page-size v-model="pageSize" :total="filtered.length" />
                <result-count
                  :current-page="currentPage"
                  :page-size="pageSize"
                  :total="filtered.length"
                />
              </v-layout>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import PageSize from "@/components/PageSize";
import ResultCount from "@/components/ResultCount";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "FAQs",
  components: {
    PageSize,
    ResultCount
  },
  data: function() {
    return {
      filter: {
        open: false,
        search: "",
        ctaTypes: []
      },
      page: 1,
      pageSize: this.defaultPageSize
    };
  },
  computed: {
    ...mapGetters({
      ctaTypes: "faqs/ctaTypes",
      ctaTypeLabel: "faqs/ctaTypeLabel",
      list: "faqs/list",
      reading: "faqs/reading",
      toRemove: "faqs/toRemove",
      toUpsert: "faqs/toUpsert",
      type: "types/toView",
      removing: "faqs/removing",
      defaultPageSize: "ui/defaultPageSize",
      getTypeById: "types/getById"
    }),
    filtered() {
      let filtered = this.list;
      if (this.filter.open) {
        const { search, ctaTypes } = this.filter;
        if (search) {
          filtered = filtered.filter(item => {
            const searchable = item.question + " " + item.answer;
            return searchable.toLowerCase().includes(search.toLowerCase());
          });
        }
        if (ctaTypes.length) {
          filtered = filtered.filter(item => ctaTypes.includes(item.cta_type));
        }
      }
      return filtered;
    },
    paged() {
      return this.filtered.slice(
        (this.currentPage - 1) * 10,
        this.currentPage * 10 + 10
      );
    },
    currentPage: {
      get() {
        return this.page > this.totalPages ? this.totalPages : this.page;
      },
      set(page) {
        this.page = page;
      }
    },
    totalPages() {
      return Math.ceil(this.filtered.length / 10);
    }
  },
  created() {
    if (this.type.id != this.$route.params.typeId) {
      const type = this.getTypeById(this.$route.params.typeId);
      if (type.id == this.$route.params.typeId) {
        this.setType(type);
      } else {
        this.$router.replace({ name: "Types" });
      }
    }

    this.hydrate();

    this.setPageTitle({
      pageTitle: `Questions`,
      pageSubtitle: this.type.type
    });
  },
  methods: {
    ...mapActions({
      hydrate: "faqs/hydrate",
      read: "faqs/read",
      remove: "faqs/remove",
      setPageTitle: "ui/setPageTitle",
      setToUpsert: "faqs/setToUpsert",
      setToRemove: "faqs/setToRemove",
      setType: "types/setToView"
    }),
    beginEdit(item) {
      this.setToUpsert(item);
      this.$router.push({ name: "FAQ", params: { faqId: this.toUpsert.id } });
    }
  }
};
</script>
